// src/pages/dashboard/nanny/offers/Offers.jsx
import { useState, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import api from '../../../../lib/api';
import { error, success } from '../../../../components/common/Toast';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import NegotiationModal from '../../../../components/offers/NegotiationModal';
import {
  CheckCircle,
  XCircle,
  MessageCircle,
  Clock,
  ArrowLeftRight,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { formatMoney, formatDate } from '../../../../lib/helpers';
import RatingDisplay from '../../../../components/common/RatingDisplay';

export default function NannyOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [negoOpen, setNegoOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    fetchOffers();
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();
  const scrollTo = (index) => emblaApi?.scrollTo(index);

  const fetchOffers = async () => {
    try {
      const res = await api.get('/offers/my-offers');
      const receivedOffers = res.data.data?.received || res.data.received || [];
      setOffers(receivedOffers);
    } catch {
      error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const respond = async (id, action) => {
    try {
      await api.post(`/offers/${id}/respond`, { action });
      success(
        action === 'accept' ? 'Offer accepted!' :
        action === 'reject' ? 'Offer declined' : 'Action completed'
      );
      fetchOffers();
    } catch {
      error('Action failed');
    }
  };

  const negotiate = (offer) => {
    setSelectedOffer(offer);
    setNegoOpen(true);
  };

  if (loading) return <LoadingSpinner size="lg" text="Loading your offers..." />;

  if (offers.length === 0) {
    return (
      <div className="text-center py-24 sm:py-32 px-6">
        <MessageCircle size={100} className="mx-auto text-gray-300 mb-10 opacity-40" />
        <h2 className="text-4xl sm:text-5xl font-bold text-gray-700">No offers yet</h2>
        <p className="text-xl sm:text-2xl text-gray-500 mt-6">
          Complete your profile to start receiving job offers!
        </p>
      </div>
    );
  }

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-center text-4xl sm:text-6xl font-bold mb-16 sm:mb-20 text-primary">Job Offers</h1>

        <div className="relative">
          {/* Carousel */}
          <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
            <div className="flex">
              {offers.map((offer) => {
                const customer = offer.made_by;
                const hasNegotiations = offer.negotiations?.length > 0;
                const latestNegotiation = offer.negotiations?.[offer.negotiations.length - 1];

                return (
                  <div key={offer.id} className="flex-none w-full min-w-0 px-4 sm:px-8">
                    <div
                      className={`bg-white rounded-3xl shadow-2xl overflow-hidden border-l-8 sm:border-l-12 transition-all
                        ${offer.status === 'pending' ? 'border-primary' :
                          offer.status === 'accepted' ? 'border-green-500' :
                          'border-red-500'}`}
                    >
                      <div className="p-8 sm:p-12 lg:p-16">
                        {/* Header */}
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 sm:gap-10 mb-10 sm:mb-12">
                          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-10 w-full">
                            <div className="w-28 h-28 sm:w-36 sm:h-36 bg-gradient-to-br from-secondary to-primary rounded-3xl flex items-center justify-center text-white text-5xl sm:text-7xl font-bold shadow-2xl flex-shrink-0">
                              { customer.profile_picture && !customer.profile_picture.includes('default-avatar') ? 
                                <img
                                  src={process.env.REACT_APP_IMAGE_URL+customer?.profile_picture}
                                  alt={`${customer.full_name}`}
                                  className="w-full h-full rounded-3xl object-cover"
                                /> : 
                                customer?.full_name?.[0]?.toUpperCase() || 'U'
                              }
                            </div>
                            <div className="text-center sm:text-left flex-1">
                              <h3 className="text-3xl sm:text-5xl font-bold">{customer?.full_name}</h3>
                              <div className="my-4"><RatingDisplay rating={customer?.rating} /></div>                        
                              <p className="text-lg sm:text-2xl text-gray-500 mt-3">Offer #{offer.id}</p>
                            </div>
                          </div>

                          <div className="text-center lg:text-right w-full lg:w-auto">
                            <p className="text-5xl sm:text-7xl font-black text-primary">
                              {formatMoney(offer.amount)}
                            </p>
                            <p className="text-2xl sm:text-3xl text-gray-600">per month</p>
                          </div>
                        </div>

                        {/* Conditions */}
                        <div className="bg-gray-50 p-8 sm:p-10 rounded-3xl mb-10 sm:mb-12">
                          <p className="text-lg sm:text-2xl text-gray-800 leading-relaxed whitespace-pre-line">
                            {offer.conditions || 'No additional conditions specified.'}
                          </p>
                        </div>

                        {/* Negotiation Alert */}
                        {offer.status === 'pending' && hasNegotiations && (
                          <div className="p-8 sm:p-10 bg-orange-50 rounded-3xl border-4 border-orange-300 mb-10 sm:mb-12">
                            <p className="text-2xl sm:text-3xl font-bold text-orange-800 flex items-center gap-4 sm:gap-5 justify-center sm:justify-start">
                              <ArrowLeftRight size={32} sm={40} /> Negotiation in Progress
                            </p>
                            <p className="text-xl sm:text-2xl text-orange-700 mt-6 text-center sm:text-left">
                              Latest counter:{' '}
                              <span className="font-black text-2xl sm:text-3xl">
                                {formatMoney(latestNegotiation.proposed_amount)}
                              </span>
                              {latestNegotiation.proposed_terms && (
                                <span className="block mt-4 italic text-lg sm:text-xl">
                                  "{latestNegotiation.proposed_terms}"
                                </span>
                              )}
                            </p>
                          </div>
                        )}

                        {/* Action Buttons - Desktop */}
                        {offer.status === 'pending' && (
                          <div className="hidden lg:flex flex-col sm:flex-row gap-6 sm:gap-8 justify-center mt-12 sm:mt-16">
                            <button
                              onClick={() => respond(offer.id, 'accept')}
                              className="px-12 sm:px-16 py-6 sm:py-7 bg-green-600 hover:bg-green-700 text-white font-bold text-xl sm:text-2xl rounded-3xl shadow-2xl transition transform hover:scale-105 flex items-center gap-4"
                            >
                              <CheckCircle size={36} sm={40} /> Accept Offer
                            </button>

                            <button
                              onClick={() => negotiate(offer)}
                              className="px-12 sm:px-16 py-6 sm:py-7 bg-orange-600 hover:bg-orange-700 text-white font-bold text-xl sm:text-2xl rounded-3xl shadow-2xl transition transform hover:scale-105 flex items-center justify-center gap-4"
                            >
                              <MessageCircle size={36} sm={40} /> Negotiate
                            </button>

                            <button
                              onClick={() => respond(offer.id, 'reject')}
                              className="px-12 sm:px-16 py-6 sm:py-7 bg-red-600 hover:bg-red-700 text-white font-bold text-xl sm:text-2xl rounded-3xl shadow-2xl transition transform hover:scale-105 flex items-center justify-center gap-4"
                            >
                              <XCircle size={36} sm={40} /> Decline Offer
                            </button>
                          </div>
                        )}

                        {/* Mobile Buttons */}
                        {offer.status === 'pending' && (
                          <div className="flex lg:hidden flex-col gap-6 mt-10">
                            <button
                              onClick={() => respond(offer.id, 'accept')}
                              className="py-6 bg-green-600 hover:bg-green-700 text-white font-bold text-2xl rounded-3xl shadow-2xl flex items-center justify-center gap-4"
                            >
                              <CheckCircle size={36} /> Accept
                            </button>
                            <button
                              onClick={() => negotiate(offer)}
                              className="py-6 bg-orange-600 hover:bg-orange-700 text-white font-bold text-2xl rounded-3xl shadow-2xl flex items-center justify-center gap-4"
                            >
                              <MessageCircle size={36} /> Negotiate
                            </button>
                            <button
                              onClick={() => respond(offer.id, 'reject')}
                              className="py-6 bg-red-600 hover:bg-red-700 text-white font-bold text-2xl rounded-3xl shadow-2xl flex items-center justify-center gap-4"
                            >
                              <XCircle size={36} /> Decline
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Negotiation History */}
                      {hasNegotiations && (
                        <div className="bg-gray-50 px-8 sm:px-16 py-10 sm:py-12 border-t-4 border-gray-300">
                          <h4 className="text-3xl sm:text-4xl font-bold mb-8 sm:mb-10 flex items-center gap-4 sm:gap-5">
                            <ArrowLeftRight size={36} sm={40} className="text-primary" />
                            Negotiation History
                          </h4>
                          <div className="space-y-6 sm:space-y-8">
                            {offer.negotiations.map((nego) => (
                              <div key={nego.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-6 border-b last:border-0 gap-4">
                                <div className="text-center sm:text-left">
                                  <p className="text-3xl sm:text-4xl font-bold text-primary">
                                    {formatMoney(nego.proposed_amount)}
                                  </p>
                                  {nego.proposed_terms && (
                                    <p className="text-lg sm:text-xl text-gray-600 italic mt-3">
                                      "{nego.proposed_terms}"
                                    </p>
                                  )}
                                </div>
                                <p className="text-gray-500 text-base sm:text-lg">
                                  {formatDate(nego.created_at, true)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={scrollPrev}
            disabled={selectedIndex === 0}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 -translate-x-12 sm:-translate-x-20 bg-white/95 hover:bg-white rounded-full p-4 sm:p-6 shadow-2xl transition disabled:opacity-30"
          >
            <ChevronLeft size={48} sm={56} />
          </button>

          <button
            onClick={scrollNext}
            disabled={selectedIndex === offers.length - 1}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 translate-x-12 sm:translate-x-20 bg-white/95 hover:bg-white rounded-full p-4 sm:p-6 shadow-2xl transition disabled:opacity-30"
          >
            <ChevronRight size={48} sm={56} />
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-4 sm:gap-5 mt-16 sm:mt-20">
          {offers.map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollTo(idx)}
              className={`h-4 sm:h-5 rounded-full transition-all duration-300 ${
                idx === selectedIndex
                  ? 'bg-primary w-16 sm:w-20'
                  : 'bg-gray-300 w-4 sm:w-5 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>

        <NegotiationModal
          offer={selectedOffer}
          isOpen={negoOpen}
          onClose={() => {
            setNegoOpen(false);
            setSelectedOffer(null);
          }}
          onSuccess={fetchOffers}
        />
      </div>
    </div>
  );
}